import React, { useState, useRef } from 'react';
import { Box, Typography, Paper, Button, CircularProgress } from '@mui/material';
import useWebcamMonitor from '../hooks/useWebcamMonitor';
import { apiFetch } from '../api';

const FaceLogin = ({ onLoginSuccess }) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const { videoRef, isStreaming, error: webcamError } = useWebcamMonitor({
    video: { width: 640, height: 480, facingMode: 'user' }
  });

  const captureFrame = () => {
    if (!videoRef.current) return null;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    return canvas.toDataURL('image/jpeg');
  };

  const verifyFace = async () => {
    try {
      setIsVerifying(true);
      setError(null);
      const userId = localStorage.getItem('registeredUserId');
      if (!userId) {
        throw new Error('No registered user found. Please register first.');
      }
      const screenshot = captureFrame();
      if (!screenshot) throw new Error('Failed to capture image');
      const res = await apiFetch('/api/verify-face', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          image: screenshot
        })
      });
      const data = await res.json();
      if (!data.success) throw new Error('Face verification failed. Please try again.');
      setConfidence(data.confidence);
      onLoginSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, margin: '0 auto' }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Face Login
        </Typography>
        <Typography paragraph>
          Please position your face in the center of the frame. Make sure you are in a well-lit area
          and your face is clearly visible.
        </Typography>

        {webcamError && (
          <Typography color="error" gutterBottom>
            Webcam Error: {webcamError.message}
          </Typography>
        )}
        {error && (
          <Typography color="error" gutterBottom>
            Verification Error: {error}
          </Typography>
        )}
        {confidence && (
          <Typography color="success.main" gutterBottom>
            Match Confidence: {confidence.toFixed(2)}%
          </Typography>
        )}

        <Box sx={{ mb: 2, position: 'relative', width: '100%', maxWidth: '640px', margin: '0 auto' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ 
              width: '100%',
              height: 'auto',
              border: '3px solid #1976d2',
              borderRadius: '8px'
            }}
          />
          <Box sx={{ 
            position: 'absolute', 
            top: 10, 
            right: 10, 
            backgroundColor: '#1976d2',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.8rem'
          }}>
            {isStreaming ? 'Webcam Active' : 'Webcam Inactive'}
          </Box>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={verifyFace}
          disabled={isVerifying || !isStreaming}
          fullWidth
        >
          {isVerifying ? <CircularProgress size={24} /> : 'Verify Face'}
        </Button>
      </Paper>
    </Box>
  );
};

export default FaceLogin; 